import React from 'react'
import { useSelector } from 'react-redux'

import { FeatureTooltip, Flex, Icon, Link, StatusBadge, Text } from '@driftt/dds'
import { faSparkles } from '@driftt/dds.icons/solid/faSparkles'

import AccountFitScore from 'components/AccountFitScore'
import { CompanyData, ScoreBreakdown } from 'modules/accountActivity/domain'
import { getCurrentOrg } from 'selectors'
import { segmentTrack,TrackType } from 'utils/tracking'

const BreakdownItem = ({category, value, valueRank}: {category: string, value: string, valueRank: "success" | "warning" | "error" | "inactive"}) => {
  return (
      <Flex justifyContent='space-between' width='100%'>
        <Text>{category}</Text>
        <StatusBadge size='medium' variant={valueRank}>{value}</StatusBadge>
      </Flex>
  )
}

const AccountFitBreakdownTooltip = ({ companyData, score, scoreBreakdown }: {
  companyData: CompanyData | undefined
  score: number | undefined
  scoreBreakdown: ScoreBreakdown | undefined
}) => {
  const org = useSelector(getCurrentOrg)

  const getVariant = (featureScore: string | undefined) => {
    switch (featureScore) {
      case undefined:
        return 'inactive'
      case 'Poor':
        return 'error'
      case 'Fair':
        return 'warning'
      default:
        return 'success'
  }
  }

  const track = () => {
    segmentTrack(
      TrackType.ACCOUNT_FIT_TOOLTIP_CLICK, 
      {
        orgId: org.id,
        score: score
      })
  }

  const triggerStyles = 
    {
      '&:hover': {
        textDecoration: 'underline'
      }
    }

  return (
    <FeatureTooltip.Root>
      <FeatureTooltip.Trigger>
        <Link onClick={track} css={triggerStyles}>
          <AccountFitScore score={score}/>
        </Link>
      </FeatureTooltip.Trigger>
      <FeatureTooltip.Content orientation='vertical' side='right' css={{paddingTop: '16px'}}>
        <Flex direction='column' gap={0}>
          <Text variant='bodyBold'>Account Fit</Text> 
          <Text variant='metadata' css={{marginBottom: '16px'}}>{companyData?.name}</Text>
          <AccountFitScore score={score}/>
          <Flex css={{marginTop: '16px'}}>
            <Icon icon={faSparkles} color="purple7"/>
            <Text variant='metadata'>{`Drift's account fit score is powered by AI.`}</Text>
          </Flex>
          <Flex 
            direction='column' 
            width='100%' 
            gap={0}
            css={{
              marginTop: '16px',
              '& > *': {
                borderBottom: '1px solid $gray2',
                paddingTop: '8px',
                paddingBottom: '8px',
                '&:last-child': {
                  borderBottom: 'none'
                }
              },
            }}>
            <BreakdownItem 
              category='Location' 
              value={companyData?.country ?? 'Unknown'} 
              valueRank={getVariant(scoreBreakdown?.Country)}/>
            <BreakdownItem 
              category='Industry' 
              value={companyData?.industry ?? 'Unknown'} 
              valueRank={getVariant(scoreBreakdown?.Industry)}/>
            <BreakdownItem 
              category='Employee count' 
              value={companyData?.employeeCountRange ?? 'Unknown'} 
              valueRank={getVariant(scoreBreakdown?.EmployeeCount)}/>
          </Flex>
        </Flex>
      </FeatureTooltip.Content>
    </FeatureTooltip.Root>)
}
export default AccountFitBreakdownTooltip